// What a work card carries besides its steps. Nothing is written per card: reference material
// is attached once, at the lowest level where it can be reused, and a card pulls together
// whatever applies to its steps and its items, with duplicates removed.
//
//   - An SOP attaches to an operation. Every card with that operation shows it beside the step.
//   - A checklist line attaches to an operation or to a catalog item. It may carry a "show
//     when" rule over the item's values ("finished end, no fasteners through this face" only
//     where a side is finished), and it says whether it must be ticked before the card closes.

import { calc, objectScope, truthy, type Value } from './expr.ts';

export interface Sop {
  id: string;
  operation_id: string;
  title: string;
  url?: string | null;
  sort?: number | null;
}

export interface ChecklistLine {
  id: string;
  operation_id?: string | null;
  catalog_item_id?: string | null;
  text: string;
  /** Has to be ticked before the card can close. */
  required: boolean;
  /** A formula over the item's values. Empty means always. On an operation line it is tried against each of the card's items. */
  show_when?: string | null;
  sort?: number | null;
}

export interface CardItemLike {
  id: string;
  number: string;
  catalog_item_id: string | null;
  values: Record<string, Value | number>;
}

export interface CardLike {
  steps: { operation_id: string | null }[];
  items: CardItemLike[];
}

export interface AssembledLine {
  line: ChecklistLine;
  /** The items it is shown for, by number. Empty for an operation line with no rule. */
  item_numbers: string[];
}

export interface AssembledCard {
  /** One entry per SOP, however many of the card's steps use its operation. */
  sops: Sop[];
  checklist: AssembledLine[];
  /** Rules that could not be worked out, so a broken rule is seen rather than silently hiding a line. */
  problems: string[];
}

const bySort = <T extends { sort?: number | null; id: string }>(a: T, b: T) => (a.sort ?? 0) - (b.sort ?? 0) || a.id.localeCompare(b.id);

/** Everything that applies to this card's steps and items, each thing once. */
export function assembleCard(card: CardLike, sops: Sop[], lines: ChecklistLine[]): AssembledCard {
  const ops = new Set(card.steps.map((s) => s.operation_id).filter(Boolean) as string[]);
  const out: AssembledCard = { sops: [], checklist: [], problems: [] };

  const seenSop = new Set<string>();
  for (const s of [...sops].sort(bySort)) {
    if (!ops.has(s.operation_id) || seenSop.has(s.id)) continue;
    seenSop.add(s.id);
    out.sops.push(s);
  }

  const applies = (line: ChecklistLine, item: CardItemLike): boolean => {
    const rule = line.show_when?.trim();
    if (!rule) return true;
    try {
      return truthy(calc(rule, objectScope(item.values)));
    } catch (e) {
      const why = `"${line.text}": its rule could not be worked out for ${item.number} (${e instanceof Error ? e.message : String(e)}), so it is shown.`;
      if (!out.problems.includes(why)) out.problems.push(why);
      return true; // a rule that cannot be read must not hide a line
    }
  };

  const seenLine = new Set<string>();
  for (const line of [...lines].sort(bySort)) {
    if (seenLine.has(line.id)) continue;
    if (line.catalog_item_id) {
      const hits = card.items.filter((i) => i.catalog_item_id === line.catalog_item_id && applies(line, i));
      if (hits.length === 0) continue;
      seenLine.add(line.id);
      out.checklist.push({ line, item_numbers: hits.map((i) => i.number) });
    } else if (line.operation_id && ops.has(line.operation_id)) {
      if (!line.show_when?.trim()) {
        seenLine.add(line.id);
        out.checklist.push({ line, item_numbers: [] });
        continue;
      }
      const hits = card.items.filter((i) => applies(line, i));
      if (hits.length === 0) continue;
      seenLine.add(line.id);
      out.checklist.push({ line, item_numbers: hits.map((i) => i.number) });
    }
  }
  return out;
}

/**
 * A card with a step whose operation needs a cut sheet is waiting on it until a file of a
 * cut-sheet kind is attached to the card. A flag, never a hold: the card can still be pulled.
 */
export function waitingOnCutSheet(
  steps: { operation_id: string | null }[],
  operationsNeedingOne: Iterable<string>,
  cardFiles: { kind?: string | null }[],
  cutSheetKinds: string[],
): boolean {
  const need = new Set(operationsNeedingOne);
  if (!steps.some((s) => s.operation_id !== null && need.has(s.operation_id))) return false;
  const kinds = new Set(cutSheetKinds.map((k) => k.trim().toLowerCase()));
  return !cardFiles.some((f) => kinds.has(String(f.kind ?? '').trim().toLowerCase()));
}

/** A card can close when every line that must be ticked has been. */
export function cardCanClose(checklist: AssembledLine[], tickedLineIds: Iterable<string>): { ok: boolean; missing: ChecklistLine[] } {
  const ticked = new Set(tickedLineIds);
  const missing = checklist.filter((c) => c.line.required && !ticked.has(c.line.id)).map((c) => c.line);
  return { ok: missing.length === 0, missing };
}
